import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React, {useMemo} from 'react';
import {useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';
import {twMerge} from 'tailwind-merge';

import {useSelector} from '../../../state/hooks';
import {PanelBankContextProvider} from '../../../state/panelbank/context';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import {Ref as PanelBankSectionConfigRef} from '../../../state/views/panelBankSectionConfig/types';
import {useRunSelectorResizingContextMaybe} from '../../MultiRunWorkspace/RunSelectorResizingContext';
import {usePanelBankSectionsScrollContext} from '../../PanelBank/contextProviders/PanelBankSectionsScrollContextProvider';
import {PanelCompRedux} from '../../PanelCompRedux';
import {useWorkspaceInheritedSettingsForPanel} from '../../WorkspaceDrawer/Settings/hooks/useWorkspaceInheritedSettingsForPanel';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useConfigXBounds} from '../hooks/hooks';
import {PanelContextProvider} from '../PanelContextProvider';
import {PanelEditorContainer} from '../PanelEditor/PanelEditorContainer';
import {PanelIDContextProvider} from '../PanelIDContext';
import {SharedPanelStateContextProvider} from '../SharedPanelStateContext';
import {usePanelViewTypeClass} from '../usePanelViewTypeClass';
import {FullScreenPanelHeader} from './FullScreenPanelHeader';
import {getPanelLinkById} from './fullScreenUtils';
import {useZoomStateFromURL} from './useZoomStateFromURL';

// FullScreenPanelBoundary is a contaienr that ensures panel contents don't
// visually overflow. Scrollbars are hidden so that they don't cause layout
// shifts to panel contents (this can lead to 'ResizeObserver' loops).
//
// TODO: Migrate from a styled component to a custom Tailwind utility class.
const FullScreenPanelBoundary = styled.div`
  overflow: auto;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FullScreenPanelContainer = ({
  isProjectReadOnly,
  className,
  panelRef,
  panelBankSectionConfigRef,
}: {
  isProjectReadOnly: boolean; // whether user can edit project level resources (e.g. reports)
  className?: string;
  panelRef: PanelRef;
  panelBankSectionConfigRef: PanelBankSectionConfigRef;
}) => {
  const history = useHistory();

  // Note: ensure this component is wrapped in a SectionSettingsContextProvider
  const inheritedSettings = useWorkspaceInheritedSettingsForPanel();
  const configXBounds = useConfigXBounds(panelRef);
  const runSelectorResizingContext = useRunSelectorResizingContextMaybe();
  const {shouldHidePanelContents} = useWorkspaceDrawerContext();
  const shouldRenderContent =
    !shouldHidePanelContents && !runSelectorResizingContext?.isResizing;

  // This ensures that we scroll to the correct section when user clicks
  // on the back button in the full screen panel.
  const panelBankSectionConfigId = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].__id__
  );
  const {scrollToStableSectionIdRef} = usePanelBankSectionsScrollContext();
  scrollToStableSectionIdRef.current = panelBankSectionConfigId;

  // This is currently used to track panel setting analytics
  const stablePanelId = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].__id__
  );

  const panelType = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].viewType
  );

  const isPanelAuto = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].isAuto
  );

  const panelViewTypeClass = usePanelViewTypeClass(panelType);

  const panelClassNames = useMemo(() => {
    return clsx('editable-panel', panelViewTypeClass, {
      'editable-panel-no-content': !shouldRenderContent,
    });
  }, [panelViewTypeClass, shouldRenderContent]);

  // See note about full screen panels in SharedPanelStateContextProvider
  const zoomStateFromURL = useZoomStateFromURL();

  return (
    <PanelBankContextProvider>
      <PanelContextProvider
        isProjectReadOnly={isProjectReadOnly}
        panelBankSectionConfigRef={panelBankSectionConfigRef}
        panelRef={panelRef}
        inheritedSettings={inheritedSettings}>
        <PanelIDContextProvider stablePanelId={stablePanelId}>
          <SharedPanelStateContextProvider
            config={configXBounds}
            zoomState={zoomStateFromURL}>
            <PanelEditorContainer
              currentHeight={0} // TODO: make currentHeight default to 0 in component and make prop optional
              onOK={() => {
                // If panel is auto, then the current URL is based on the panel display name.
                // When a panel is edited, its display name may change (e.g. user updates
                // panel title or y axis metrics). We must redirect to the ID-based URL to
                // avoid ending up on a "panel not found" page. This is safe to do because
                // the panel will no longer be auto once it's edited.
                if (isPanelAuto) {
                  history.replace(getPanelLinkById(stablePanelId));
                }
              }}
            />
            <TailwindContents>
              <FullScreenPanelBoundary
                className={twMerge(
                  panelClassNames,
                  'px-16 py-12',
                  'flex flex-col justify-start',
                  className
                )}
                data-test="full-screen-panel-container">
                <FullScreenPanelHeader
                  className="flex-none"
                  panelBankSectionConfigRef={panelBankSectionConfigRef}
                  panelType={panelType}
                />
                <div
                  className={twMerge(
                    'flex flex-col',
                    // NOTE: 'overflow-visible' ensures wave loaders for line
                    // plots, which overflow their container, remain visible.
                    'flex-auto justify-center overflow-visible',
                    // The fullscreen panel content height should be constrained
                    // so that there is never a scrollbar created on the page.
                    //
                    // The available panel height is defined as follows:
                    //
                    //    = screen size
                    //      - top nav. bar
                    //      - sidebar (<= medium screen)
                    //      - workspace bar
                    //      - fullscreen panel border
                    //      - fullscreen panel padding
                    //      - fullscreen panel header (56px if <= medium screen else 88px)
                    //
                    // FIXME: When viewing table panels in fullscreen mode,
                    // shrinking the window *upwards on small screens* will
                    // cause a page-level scrollbar to appear. There is only 1px
                    // of overflow and it's unclear where it's coming from.
                    'max-h-[calc(100vh-60px-55px-57px-2px-24px-56px)]',
                    'md:max-h-[calc(100vh-60px-57px-2px-24px-88px)]'
                  )}
                  id={'editable-panel-' + stablePanelId}>
                  <PanelCompRedux
                    configMode={false}
                    currentHeight={0}
                    dimensions={undefined as any}
                    panelRef={panelRef}
                  />
                </div>
              </FullScreenPanelBoundary>
            </TailwindContents>
          </SharedPanelStateContextProvider>
        </PanelIDContextProvider>
      </PanelContextProvider>
    </PanelBankContextProvider>
  );
};
